import React, { useState, useEffect } from 'react';
import { FileText, User, Shield, Clock, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/security/audit-log?limit=100');
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    if (actionType.includes('fraud')) return <Shield className="h-4 w-4" />;
    if (actionType.includes('user')) return <User className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const getActionColor = (actionType) => {
    if (actionType.includes('banned')) return 'text-[#FF3B5C] bg-[#FF3B5C]/10';
    if (actionType.includes('approved')) return 'text-[#00FF88] bg-[#00FF88]/10';
    if (actionType.includes('flagged')) return 'text-[#FFB800] bg-[#FFB800]/10';
    return 'text-[#00FFFF] bg-[#00FFFF]/10';
  };

  const getActionLabel = (actionType) => {
    const labels = {
      'fraud_decision_approved': 'Compte Approuvé',
      'fraud_decision_flagged': 'Compte Signalé',
      'fraud_decision_banned': 'Compte Banni',
      'manual_fraud_check': 'Analyse Anti-Fraude',
      'modify_commission': 'Modification Commission'
    };
    return labels[actionType] || actionType;
  };

  const filteredLogs = filter === 'all' 
    ? logs 
    : logs.filter(log => log.action_type.includes(filter));

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00FFFF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-white flex items-center space-x-2">
          <FileText className="h-6 w-6 text-[#00FFFF]" />
          <span>Journal d'Audit ({filteredLogs.length})</span>
        </h3>
        
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-[#B8B8BA]" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#222226] text-white px-3 py-1 rounded border border-[#3A3A3E] focus:border-[#00FFFF] outline-none text-sm"
          >
            <option value="all">Toutes les actions</option>
            <option value="fraud">Anti-Fraude</option>
            <option value="commission">Commissions</option>
            <option value="banned">Bannissements</option>
          </select>
        </div>
      </div>

      {filteredLogs.length === 0 ? (
        <div className="bg-[#222226] p-8 rounded-lg text-center text-[#B8B8BA]">
          Aucune action enregistrée
        </div>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((log, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.02 }}
              className="bg-[#222226] p-4 rounded-lg hover:bg-[#2A2A2E] transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className={`p-2 rounded ${getActionColor(log.action_type)}`}>
                    {getActionIcon(log.action_type)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-white font-medium">
                        {getActionLabel(log.action_type)}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getActionColor(log.action_type)}`}>
                        {log.action_type}
                      </span>
                    </div>
                    
                    <div className="text-[#B8B8BA] text-sm space-y-1">
                      <div>
                        👤 Admin: <span className="text-white">{log.admin_email}</span>
                      </div>
                      {log.target_user_email !== 'system' && (
                        <div>
                          🎯 Cible: <span className="text-white">{log.target_user_email}</span>
                        </div>
                      )}
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="text-xs bg-[#1A1A1D] p-2 rounded mt-2">
                          {log.details.admin_notes && (
                            <div>📝 {log.details.admin_notes}</div>
                          )}
                          {log.details.decision && (
                            <div>Décision: {log.details.decision}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="text-right text-xs text-[#6B6B6E]">
                  <div className="flex items-center space-x-1">
                    <Clock className="h-3 w-3" />
                    <span>{new Date(log.timestamp).toLocaleString('fr-FR')}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditLog;
